/**
 * useTodoReorderGesture — le moteur du glisser de « À faire » : un geste, l'état d'affichage qu'il
 * produit (`ActivitiesReorder`), et l'ordre final remis à `onReorder`.
 *
 * Le geste du legacy (`TodoListScreen.js`, `DraggableFlatList`), sur les deux plateformes :
 *  - **natif** : un appui long (300 ms, le `delayLongPress` de `RealizationsListItem.js`) n'importe
 *    où sur la ligne, puis le glisser. Bouger avant les 300 ms fait défiler la page, comme avant ;
 *  - **web** : la poignée, à gauche de la ligne — l'appui long n'y existe pas. Partout ailleurs, la
 *    ligne se touche (elle lance l'activité) et la page défile.
 *  - une vibration au début et à la fin, comme `onDragBegin` / `onDragEnd`.
 *
 * Pas de `FlatList` glissante : les lignes vivent dans une carte-liste, elle-même dans le
 * `ScrollView` de la page (une liste virtualisée imbriquée s'y disputerait le geste, la raison de
 * `StageDragList.js`). Le geste est posé UNE fois, sur la carte, et pas sur chaque ligne : la ligne
 * qu'on déplace quitte sa place pour l'emplacement d'arrivée (`<CardListDropSlot>`), et un geste
 * posé sur elle mourrait avec son ancienne place. La ligne sous le doigt se lit sur les hauteurs
 * mesurées (`todoReorder.ts`).
 *
 * `react-native-gesture-handler`, déjà permis au design system (`CLAUDE.md`) et monté à la racine de
 * l'app : aucune dépendance de plus. Ses rappels tournent sur le fil JS (`runOnJS`) : on change un
 * état React à chaque cran, pas une valeur animée à chaque image.
 */
import { useCallback, useMemo, useRef, useState } from "react"
import { Platform, Vibration, type LayoutChangeEvent } from "react-native"
import { Gesture } from "react-native-gesture-handler"

import { type ActivitiesReorder, type ActivityId } from "./ActivitiesSection"
import { moveItem, reorderTarget, rowIndexAt } from "./todoReorder"

/** Le `delayLongPress` du legacy (`RealizationsListItem.js`). */
export const REORDER_LONG_PRESS_MS = 300
/** La vibration du legacy, au début et à la fin du glisser. */
const VIBRATION_MS = 80
/** La bande de la poignée, à gauche de la ligne, sur le web : un appui hors d'elle ne glisse pas. */
export const REORDER_HANDLE_WIDTH = 36

export const REORDER_BY_HANDLE = Platform.OS === "web"

type Id = ActivityId

export type TodoReorderDrag = ActivitiesReorder

type Pending = { id: Id; from: number }
type Dragging = Pending & { to: number }

export function useTodoReorderGesture(ids: readonly Id[], onReorder: ((ids: Id[]) => void) | undefined) {
    const enabled = onReorder != null && ids.length > 1
    const [drag, setDrag] = useState<TodoReorderDrag | null>(null)

    // Le geste est créé une fois par activation et lit la liste à travers des refs : sans elles, il
    // garderait la liste de son premier rendu, et un glisser après une relecture du compte
    // réordonnerait des lignes périmées.
    const heightsRef = useRef(new Map<Id, number>())
    const latestRef = useRef({ ids, onReorder })
    latestRef.current = { ids, onReorder }
    const pendingRef = useRef<Pending | null>(null)
    const draggingRef = useRef<Dragging | null>(null)

    const measure = useCallback((id: Id, event: LayoutChangeEvent) => {
        heightsRef.current.set(id, event.nativeEvent.layout.height)
    }, [])

    const gesture = useMemo(() => {
        const heights = () => latestRef.current.ids.map((id) => heightsRef.current.get(id) ?? 0)
        const reset = () => {
            pendingRef.current = null
            draggingRef.current = null
            setDrag(null)
        }

        const pan = Gesture.Pan()
            .runOnJS(true)
            .enabled(enabled)
            .maxPointers(1)
            .onTouchesDown((event, manager) => {
                const touch = event.changedTouches[0]
                const from = touch != null ? rowIndexAt(touch.y, heights()) : null
                const onHandle = !REORDER_BY_HANDLE || (touch != null && touch.x <= REORDER_HANDLE_WIDTH)
                const id = from != null ? latestRef.current.ids[from] : undefined
                if (from == null || id == null || !onHandle) {
                    pendingRef.current = null
                    manager.fail()
                    return
                }
                pendingRef.current = { id, from }
            })
            .onStart(() => {
                const pending = pendingRef.current
                if (pending == null) return
                draggingRef.current = { ...pending, to: pending.from }
                setDrag({ activityId: pending.id, toIndex: pending.from })
                Vibration.vibrate(VIBRATION_MS)
            })
            .onUpdate((event) => {
                const dragging = draggingRef.current
                if (dragging == null) return
                const to = reorderTarget(dragging.from, event.translationY, heights())
                if (to === dragging.to) return
                dragging.to = to
                setDrag({ activityId: dragging.id, toIndex: to })
            })
            .onEnd(() => {
                const dragging = draggingRef.current
                if (dragging == null) return
                Vibration.vibrate(VIBRATION_MS)
                // La liste a pu changer pendant le glisser (une relecture du compte) : la ligne se
                // relit par son identifiant, et disparue, le glisser ne range rien.
                const current = latestRef.current.ids
                const from = current.indexOf(dragging.id)
                if (from < 0 || dragging.to === from) return
                latestRef.current.onReorder?.(moveItem(current, from, dragging.to))
            })
            .onFinalize(reset)

        return REORDER_BY_HANDLE ? pan : pan.activateAfterLongPress(REORDER_LONG_PRESS_MS)
    }, [enabled])

    return { enabled, gesture, drag: enabled ? drag : null, measure }
}
